import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarDays, Clock, MapPin, Check, X, AlertTriangle } from "lucide-react";
import { PageDecor } from "../components/decor/PageDecor";
import RetourApp from "../components/RetourApp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* « Changer la date ou annuler » : le lien de l'e-mail de confirmation d'un cours d'essai
   (école, 30.10.2026 — « ça arrive souvent que les gens annulent ou changent la date, et
   après l'école doit faire manuellement »). Le lien ne vaut que pour les essais de cette
   réservation ; la console suit d'elle-même et l'école reçoit un e-mail. */

// La réponse du serveur, ou son message d'erreur (« Ce lien n'est pas valable… »).
async function lire(r) {
  let d = {};
  try { d = await r.json(); } catch { d = {}; }
  if (!r.ok) throw new Error(typeof d.detail === "string" && d.detail ? d.detail : `Erreur ${r.status}`);
  return d;
}

function Essai({ essai, jeton, onChange }) {
  const [mode, setMode] = useState("");
  const [choix, setChoix] = useState("");
  const [motif, setMotif] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState("");
  const [fait, setFait] = useState("");

  const agir = async (action, corps) => {
    setEnCours(true);
    setErreur("");
    try {
      const d = await lire(await fetch(`${API}/essai/gerer/${encodeURIComponent(essai.id)}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jeton, ...corps }),
      }));
      onChange(d.essai);
      setFait(action === "annuler"
        ? "C'est noté : le cours d'essai est annulé. L'école est prévenue, et tu reçois un e-mail de confirmation."
        : `C'est noté : nouvelle date le ${d.essai.date_lettres}. L'école est prévenue, et tu reçois un e-mail de confirmation.`);
      setMode("");
      setChoix("");
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnCours(false);
    }
  };

  const annule = essai.etat === "annule";
  return (
    <div className="rounded-3xl p-6 mt-6" data-testid={`mon-essai-${essai.id}`}
      style={{ background: "#fff", border: "1.5px solid rgba(15,27,45,0.10)", opacity: annule ? 0.75 : 1 }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-bold tracking-widest uppercase" style={{ color: "#0E7A85" }}>
            Cours d'essai{essai.eleve ? ` de ${essai.eleve}` : ""}
          </div>
          <div className="font-display text-2xl tracking-tight mt-1">{essai.cours}</div>
        </div>
        {annule && (
          <span className="shrink-0 text-xs font-semibold rounded-full px-3 py-1" style={{ background: "#FEE2E2", color: "#B91C1C" }}
            data-testid="mon-essai-annule">Annulé</span>
        )}
      </div>
      <div className="mt-3 space-y-1.5 text-sm" style={{ textDecoration: annule ? "line-through" : "none" }}>
        <div className="flex items-center gap-2"><CalendarDays size={15} /> <strong className="first-letter:uppercase">{essai.date_lettres}</strong></div>
        {essai.heure && <div className="flex items-center gap-2 text-[var(--muted)]"><Clock size={15} /> {essai.heure}</div>}
        {essai.salle && <div className="flex items-center gap-2 text-[var(--muted)]"><MapPin size={15} /> {essai.salle}</div>}
        {essai.prix && !annule && <div className="text-[var(--muted)]">{essai.prix}</div>}
      </div>

      {fait && (
        <div className="rounded-2xl px-4 py-3 text-sm mt-4 flex gap-2" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }}
          data-testid="mon-essai-fait">
          <Check size={16} className="shrink-0 mt-0.5" /> {fait}
        </div>
      )}
      {erreur && (
        <div className="rounded-2xl px-4 py-3 text-sm mt-4 flex gap-2" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}
          data-testid="mon-essai-erreur">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {erreur}
        </div>
      )}

      {essai.etat === "passe" && (
        <p className="text-sm text-[var(--muted)] mt-4">
          Ce cours d'essai a déjà eu lieu (ou il a commencé) : il ne peut plus être changé ici.
        </p>
      )}

      {essai.etat === "prevu" && !mode && (
        <div className="flex flex-wrap gap-3 mt-5">
          <button type="button" className="btn-primary" onClick={() => { setMode("date"); setFait(""); setErreur(""); }}
            data-testid="mon-essai-changer">
            <CalendarDays size={16} /> Changer la date
          </button>
          <button type="button" className="btn-ghost" onClick={() => { setMode("annuler"); setFait(""); setErreur(""); }}
            data-testid="mon-essai-annuler">
            <X size={16} /> Annuler l'essai
          </button>
        </div>
      )}

      {essai.etat === "prevu" && mode === "date" && (
        <div className="mt-5" data-testid="mon-essai-dates">
          {essai.dates_possibles.length === 0 ? (
            <p className="text-sm text-[var(--muted)]">
              Pas d'autre séance possible pour ce cours en ce moment (vacances, fin de semestre…).
              Écris-nous à <a className="underline" href="mailto:info1000arts@gmail.com">info1000arts@gmail.com</a> :
              on trouve une solution ensemble.
            </p>
          ) : (
            <>
              <p className="text-sm text-[var(--muted)]">Choisis une autre séance du même cours :</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {essai.dates_possibles.map((d) => (
                  <button key={d.date} type="button" onClick={() => setChoix(d.date)} data-testid={`mon-essai-date-${d.date}`}
                    className="rounded-2xl px-4 py-2.5 text-sm text-left transition"
                    style={{
                      border: `1.5px solid ${choix === d.date ? "var(--primary)" : "rgba(15,27,45,0.12)"}`,
                      background: choix === d.date ? "var(--primary-tint)" : "#fff",
                    }}>
                    <span className="font-semibold first-letter:uppercase block">{d.lettres}</span>
                    {d.prix && <span className="text-xs text-[var(--muted)]">{d.prix}</span>}
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="flex flex-wrap gap-3 mt-5">
            {essai.dates_possibles.length > 0 && (
              <button type="button" className="btn-primary" disabled={!choix || enCours}
                onClick={() => agir("deplacer", { date: choix })} data-testid="mon-essai-confirmer-date">
                {enCours ? "Un instant…" : "Confirmer la nouvelle date"}
              </button>
            )}
            <button type="button" className="btn-ghost" onClick={() => { setMode(""); setChoix(""); }}>Retour</button>
          </div>
        </div>
      )}

      {essai.etat === "prevu" && mode === "annuler" && (
        <div className="mt-5" data-testid="mon-essai-annulation">
          <label className="text-sm text-[var(--muted)] block" htmlFor={`motif-${essai.id}`}>
            Un mot pour l'école ? <span className="text-xs">(facultatif)</span>
          </label>
          <textarea id={`motif-${essai.id}`} className="input-field mt-2" rows={2} maxLength={300} value={motif}
            onChange={(e) => setMotif(e.target.value)} data-testid="mon-essai-motif" />
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="button" className="btn-primary" disabled={enCours}
              onClick={() => agir("annuler", { motif })} data-testid="mon-essai-confirmer-annulation"
              style={{ background: "#B91C1C" }}>
              {enCours ? "Un instant…" : "Confirmer l'annulation"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setMode("")}>Retour</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MonEssai() {
  const [params] = useSearchParams();
  const jeton = params.get("jeton") || "";
  const [essais, setEssais] = useState(null);
  const [erreur, setErreur] = useState("");

  useEffect(() => {
    if (!jeton) {
      setErreur("Ouvre cette page depuis le lien « Changer la date ou annuler » de ton e-mail de confirmation.");
      return;
    }
    fetch(`${API}/essai/gerer?jeton=${encodeURIComponent(jeton)}`)
      .then(lire)
      .then((d) => setEssais(d.essais || []))
      .catch((e) => setErreur(e.message));
  }, [jeton]);

  const remplacer = (nouveau) => setEssais((liste) => liste.map((e) => (e.id === nouveau.id ? nouveau : e)));

  return (
    <div className="pt-28 pb-24 relative overflow-hidden" data-testid="page-mon-essai">
      <PageDecor />
      <div className="max-w-2xl mx-auto px-6 relative">
        {/* Ouvert depuis l'app (app=1) : « ← Retour à l'app » (Jen, 01.11.2026). */}
        <RetourApp />
        <div className="eyebrow mb-3">Cours d'essai</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[0.95]">
          Changer la date<br /><span style={{ color: "var(--primary)" }}>ou annuler</span>
        </h1>
        <p className="text-base text-[var(--muted)] mt-5 leading-relaxed">
          Un empêchement ? Choisis une autre séance du même cours, ou annule simplement
          l'essai. L'école est prévenue automatiquement.
        </p>

        {erreur && (
          <div className="rounded-2xl px-4 py-3 text-sm mt-8 flex gap-2" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}
            data-testid="mon-essai-erreur-page">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {erreur}
          </div>
        )}
        {!erreur && essais === null && <p className="text-[var(--muted)] mt-8">Chargement…</p>}
        {(essais || []).map((e) => <Essai key={e.id} essai={e} jeton={jeton} onChange={remplacer} />)}

        <p className="text-sm text-[var(--muted)] mt-10 leading-relaxed">
          Une question ? Écris-nous à{" "}
          <a href="mailto:info1000arts@gmail.com" className="underline hover:text-[var(--primary)]">info1000arts@gmail.com</a>{" "}
          ou appelle le +41 79 950 40 31.
        </p>
        <Link to="/" className="btn-ghost mt-6 inline-flex">Retour à l'accueil</Link>
      </div>
    </div>
  );
}
